import { formatUsd } from './utility_functions';

// Optional per-list data points a user can map during list upload.
// Keys match the columns on list_uploads_properties (see ListUploadsProperty::CUSTOM_FIELDS).
export type CustomListFieldKey =
  | 'loan_type'
  | 'initial_loan_amount'
  | 'initial_loan_year'
  | 'initial_loan_month'
  | 'initial_loan_term'
  | 'foreclosure_notice_clerk_number'
  | 'deed_volume_number'
  | 'deed_page'
  | 'mortgagor_first_name'
  | 'mortgagor_last_name'
  | 'mortgagee'
  | 'trustee'
  | 'auction_sale_time'
  | 'list_class'
  | 'estimated_unpaid_balance';

type CustomListField = {
  key: CustomListFieldKey;
  label: string;
  isCurrency?: boolean;
};

export const CUSTOM_LIST_FIELDS: CustomListField[] = [
  { key: 'estimated_unpaid_balance', label: 'Estimated Unpaid Balance', isCurrency: true },
  { key: 'loan_type', label: 'Loan Type' },
  { key: 'initial_loan_amount', label: 'Initial Loan Amount', isCurrency: true },
  { key: 'initial_loan_year', label: 'Initial Loan Year' },
  { key: 'initial_loan_month', label: 'Initial Loan Month' },
  { key: 'initial_loan_term', label: 'Initial Loan Term' },
  { key: 'foreclosure_notice_clerk_number', label: 'Clerk Number for Foreclosure Notice' },
  { key: 'deed_volume_number', label: 'Volume Number for Deed' },
  { key: 'deed_page', label: 'Page for Deed' },
  { key: 'mortgagor_first_name', label: 'Mortgagor First Name' },
  { key: 'mortgagor_last_name', label: 'Mortgagor Last Name' },
  { key: 'mortgagee', label: 'Mortgagee' },
  { key: 'trustee', label: 'Trustee' },
  { key: 'auction_sale_time', label: 'Auction Sale Time' },
  { key: 'list_class', label: 'Class' },
];

// The Decision Making summary shows these in a different order than the property details page:
// Estimated Unpaid Balance leads, and Loan Type sits right after Initial Loan Term.
const DECISION_MAKING_FIELD_ORDER: CustomListFieldKey[] = [
  'estimated_unpaid_balance',
  'initial_loan_amount',
  'initial_loan_year',
  'initial_loan_month',
  'initial_loan_term',
  'loan_type',
  'foreclosure_notice_clerk_number',
  'deed_volume_number',
  'deed_page',
  'mortgagor_first_name',
  'mortgagor_last_name',
  'mortgagee',
  'trustee',
  'auction_sale_time',
  'list_class',
];

export const DECISION_MAKING_CUSTOM_FIELDS: CustomListField[] = DECISION_MAKING_FIELD_ORDER.map(
  (key) => CUSTOM_LIST_FIELDS.find((field) => field.key === key) as CustomListField
);

export type CustomListDataRow = Partial<Record<CustomListFieldKey, string | number | null>>;

export type CustomDataItem = { name: string; value: string };

// Only fields the user actually filled in for this list/property are returned.
export const getCustomDataItems = (
  row: CustomListDataRow | null | undefined,
  fields: CustomListField[] = CUSTOM_LIST_FIELDS
): CustomDataItem[] => {
  if (!row) {
    return [];
  }
  return fields.reduce<CustomDataItem[]>((items, field) => {
    const raw = row[field.key];
    if (raw === null || raw === undefined || raw === '') {
      return items;
    }
    items.push({
      name: field.label,
      value: field.isCurrency ? formatUsd(raw) : String(raw),
    });
    return items;
  }, []);
};
